import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scroller } from 'react-scroll';
import { searchContext, userAppContext } from '../../../../context/Context';
import { useScoreBreakdownStore } from '@/store/scoreBreakdownStore';
import { usePrevalenceBreakdownStore } from '@/store/prevalenceBreakdownStore';
import { useEvidenceTimelineStore } from '@/store/evidenceTimelineStore';
import { useRightSidebarLayout } from '../context/RightSidebarLayoutContext';
import { useMappingStackStore, useCurrentMappingLevel } from '@/store/mappingStackStore';
import useScrollWindow from '@/hooks/useScrollWindow';
import { useAlertSystem } from '../components/table/hooks';
import { useTableStore } from '@/store/tableStore';
import { useMappingSidebarColumn } from '@/context/MappingSidebarColumnContext';
import { deleteFilter } from '../../../../services/savedFiltersService';
import updateParams from '../../../../utils/updateParams';
import { clearPendingSearchRedirect } from '../../../../utils/pendingSearchRedirectStorage';
import { trackBrowseTabClick } from '../../../../utils/analytics';
import { getTabList } from '../helpers/tableHelpers';
import { getVisibleSearchTabs, searchTabHasGraphs } from '../helpers/graphTabVisibility';
import { ColumnVisibilityModels } from '@/components/Table/ColumnVisibilityModels';
import { applyEntityColumnDefaults, mergeEntityColumnsIntoOrder } from '../utils/entityColumnDefaults';
import {
  MAPPING_URL_PARAM,
  encodeMappingStateForUrl,
  applyMappingStateFromSearch,
} from '@/utils/mappingUrlState';
import { isProgrammaticSearchNavigationPending } from '@/hooks/search/useSearchTabNavigation';
import { SearchContext, UserContext } from '../../../../types';
import {
  DetailGroup,
  SHOW_HISTORIAL_SCORE,
  HISTORICAL_SCORE_PEEK_HEIGHT,
  getEntityPluralLabel,
  getRowDetailContextLabel,
  getRowValueByPriority,
  groupRowDetails,
  normalizeValue,
  countItems,
  type BottomDetailTab,
} from '../utils/searchResultsHelpers';
import { formatGtpList, getGtpItemsFromRow } from '@/utils/formatGtpList';
import { clearHistoricalScoreSession } from '../components/historicalScoreSession';
import { isRelease263Enabled } from '@/config/release263Features';
import { clearBottomDetailPreservation, shouldPreserveBottomDetail } from '../utils/bottomDetailNavigation';

export function useSearchResultsState(dispatch: any) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { view, source, tab, searchItems, setSearchItems, geneContent, setGeneContent, mappingContent } =
    searchContext() as unknown as SearchContext;
  const { isAuthenticated, groups, restrictChemical } = userAppContext() as unknown as UserContext;

  const { showSuccessAlert } = useAlertSystem();
  const savedColumsApplied = useTableStore(s => s.savedColumsApplied);
  const setSavedColumsApplied = useTableStore(s => s.setSavedColumsApplied);

  const scoreBreakdownRow = useScoreBreakdownStore(s => s.scoreBreakdownRow);
  const setScoreBreakdownRow = useScoreBreakdownStore(s => s.setScoreBreakdownRow);
  const prevalenceBreakdownRow = usePrevalenceBreakdownStore(s => s.prevalenceBreakdownRow);
  const setPrevalenceBreakdownRow = usePrevalenceBreakdownStore(s => s.setPrevalenceBreakdownRow);
  const evidenceTimelineRow = useEvidenceTimelineStore(s => s.evidenceTimelineRow);
  const setEvidenceTimelineRow = useEvidenceTimelineStore(s => s.setEvidenceTimelineRow);

  const { isSidebarOpen, sidebarWidth, setSidebarOpen } = useRightSidebarLayout();
  const { isMobile, isTablet } = useScrollWindow();
  const isDesktop = !isMobile && !isTablet;

  const useDrillDownMapping = process.env.REACT_APP_USE_DRILLDOWN_MAPPING === 'true';
  const mappingStackCurrentIndex = useMappingStackStore(s => s.currentIndex);
  const mappingStackLength = useMappingStackStore(s => s.stack.length);
  const mappingStack = useMappingStackStore(s => s.stack);
  const mappingTableStateByLevelId = useMappingStackStore(s => s.tableStateByLevelId);
  const clearMappingStack = useMappingStackStore(s => s.clearFromLevel);
  const collapseMappingToRoot = useMappingStackStore(s => s.collapseToRootLevel);
  const currentMappingLevel = useCurrentMappingLevel();
  const mappingSidebar = useMappingSidebarColumn();

  // ─── State ────────────────────────────────────────────────────────────────

  const [tabIndex, setTabIndex] = useState(0);
  const [selectedView, setSelectedView] = useState<'tables' | 'graphs'>(() => {
    const display = searchParams.get('display');
    return display === 'graphs' ? 'graphs' : 'tables';
  });
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState(false);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [refreshScoresDropdown, setRefreshScoresDropdown] = useState(0);
  const [leftInfoPanelItem, setLeftInfoPanelItem] = useState<any>(null);
  const [leftSentencePanel, setLeftSentencePanel] = useState<{ content: string; allowCopy: boolean } | null>(
    null,
  );
  const [bottomDetailTab, setBottomDetailTab] = useState<BottomDetailTab>('summary');
  const bottomDetailRowRef = useRef<any>(null);
  const preferredBottomDetailTabRef = useRef<BottomDetailTab | null>(null);
  const [bottomDetailGtpOnly, setBottomDetailGtpOnly] = useState(false);
  const [bottomDetailRow, setBottomDetailRow] = useState<any>(null);
  const [bottomDetailCellValues, setBottomDetailCellValues] = useState<Record<string, any> | null>(null);
  const [bottomDetailColumnLabels, setBottomDetailColumnLabels] = useState<Record<string, string> | null>(
    null,
  );
  const [bottomDetailColumnCategories, setBottomDetailColumnCategories] = useState<Record<
    string,
    string
  > | null>(null);
  const [bottomDetailHeight, setBottomDetailHeight] = useState(300);
  const [isHistoricalEvidencePeek, setIsHistoricalEvidencePeek] = useState(false);
  const [sentenceCopied, setSentenceCopied] = useState(false);

  bottomDetailRowRef.current = bottomDetailRow;

  const bottomDetailRowTabKey = useMemo(() => {
    if (!bottomDetailRow) return '';
    const r = bottomDetailRow;
    return `${String(tab)}:${String(r?.diseaseUMLSCUI ?? '')}:${String(r?.geneSymbol ?? r?.variantStrID ?? '')}`;
  }, [bottomDetailRow, tab]);
  const [sourceWeights, setSourceWeights] = useState<Array<{ name: string; score: number }>>(() => {
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });
  const [graphsColumnModel, setGraphsColumnModel] = useState<Record<string, boolean>>({});
  const [summaryColumnModel, setSummaryColumnModel] = useState<Record<string, boolean> | undefined>(
    undefined,
  );
  const [summaryColumnOrder, setSummaryColumnOrder] = useState<string[] | undefined>(undefined);
  const [evidenceColumnModel, setEvidenceColumnModel] = useState<Record<string, boolean> | undefined>(
    undefined,
  );
  const [evidenceColumnOrder, setEvidenceColumnOrder] = useState<string[] | undefined>(undefined);
  const [activeColumnPanel, setActiveColumnPanel] = useState<'summary' | 'evidence'>(() => {
    const params = new URLSearchParams(window.location.search);
    const activePanel = params.get('activePanel');
    if (activePanel === 'evidence') return 'evidence';
    if (activePanel === 'summary') return 'summary';
    if (params.get('isEvidence') === 'true') return 'evidence';
    if (params.get('isEvidence') === 'false') return 'summary';
    return 'summary';
  });
  const [isMappingUrlHydrated, setIsMappingUrlHydrated] = useState(false);
  const [defaultFilterValue] = useState<any[]>([]);

  // ─── Refs ─────────────────────────────────────────────────────────────────

  const sentenceToCopyRef = useRef<HTMLDivElement | null>(null);
  const summaryResetRef = useRef<(() => void) | null>(null);
  const evidenceResetRef = useRef<(() => void) | null>(null);
  const skipMappingUrlParamStripRef = useRef(false);
  const mappingUrlRestoreDoneRef = useRef(false);
  const prevTabForResetRef = useRef<string | null>(null);
  const prevTabForMappingRef = useRef<string | null>(null);
  const prevMappingIndexRef = useRef(0);
  const prevSearchItemsLengthRef = useRef<number | null>(null);
  const previousDetailContextSignatureRef = useRef<string | null>(null);

  // ─── Computed ─────────────────────────────────────────────────────────────

  const isTrial = groups?.includes('ROLE_TRIAL');
  const isAcademic = groups?.includes('ROLE_ACADEMIC');
  const isInMappingView = useDrillDownMapping && mappingStackLength > 0 && mappingStackCurrentIndex > 0;
  const useMappingSidebarState = isInMappingView && mappingSidebar.state != null;
  const isFullRecordMode = searchParams.get('fullRecord') === '1';

  const tabList = useMemo(() => getTabList(view as string, tab as string), [view, tab]);

  const searchItemsEntityPlural = useMemo(
    () => getEntityPluralLabel(view as string | undefined, searchItems ?? []),
    [view, searchItems],
  );

  const groupedBottomDetails = useMemo<DetailGroup[]>(() => {
    if (bottomDetailCellValues && Object.keys(bottomDetailCellValues).length > 0) {
      return groupRowDetails(
        bottomDetailCellValues,
        bottomDetailColumnLabels,
        bottomDetailColumnCategories,
        tab as string,
      );
    }
    return groupRowDetails(bottomDetailRow, null, null, tab as string);
  }, [bottomDetailRow, bottomDetailCellValues, bottomDetailColumnLabels, bottomDetailColumnCategories, tab]);

  const relationSummary = useMemo(() => {
    const row = {
      ...((bottomDetailRow ?? {}) as Record<string, any>),
      ...((bottomDetailCellValues ?? {}) as Record<string, any>),
    };
    const currentTab = String(tab ?? '').toUpperCase();

    const geneEntity =
      getRowValueByPriority(row, ['geneSymbol', 'gene_symbol', 'geneName', 'symbol'], ['gene']) ?? 'N/A';
    const diseaseEntity =
      getRowValueByPriority(row, ['diseaseName', 'disease_name', 'diseaseId'], ['disease']) ?? 'N/A';
    const variantEntity =
      getRowValueByPriority(
        row,
        ['variantStrID', 'strID', 'dbSNP', 'hgvs'],
        ['variant', 'strid', 'dbsnp', 'hgv'],
      ) ?? 'N/A';
    const chemicalEntity =
      getRowValueByPriority(row, ['chemPrefName', 'chemID', 'chemIDs'], ['chem']) ?? 'N/A';
    const disease1Entity =
      getRowValueByPriority(
        row,
        ['disease1_Name', 'disease1Name', 'disease_1', 'disease1', 'diseaseName1'],
        ['disease1'],
      ) ?? 'N/A';
    const disease2Entity =
      getRowValueByPriority(
        row,
        ['disease2_Name', 'disease2Name', 'disease_2', 'disease2', 'diseaseName2'],
        ['disease2'],
      ) ?? 'N/A';

    let leftEntity: any = geneEntity;
    let rightEntity: any = diseaseEntity;
    let showRelationArrow = true;

    if (bottomDetailGtpOnly) {
      leftEntity = geneEntity;
      rightEntity = null;
      showRelationArrow = false;
    } else if (currentTab === 'DDA') {
      leftEntity = disease1Entity !== 'N/A' ? disease1Entity : diseaseEntity;
      rightEntity = disease2Entity !== 'N/A' ? disease2Entity : diseaseEntity;
    } else if (currentTab === 'DISEASES') {
      leftEntity = diseaseEntity;
      rightEntity = null;
      showRelationArrow = false;
    } else if (currentTab === 'VARIANTS') {
      leftEntity = variantEntity;
      rightEntity = null;
      showRelationArrow = false;
    } else if (currentTab === 'CHEMICALS') {
      leftEntity = chemicalEntity;
      rightEntity = null;
      showRelationArrow = false;
    } else if (currentTab === 'GENES') {
      leftEntity = geneEntity;
      rightEntity = null;
      showRelationArrow = false;
    } else if (currentTab === 'VDA') {
      leftEntity = variantEntity;
      rightEntity = diseaseEntity;
      showRelationArrow = true;
    }

    const diseaseType = getRowValueByPriority(row, ['geneDescription'], ['gene_description']);
    const score = getRowValueByPriority(row, ['score'], ['score']);
    const pmids = getRowValueByPriority(
      row,
      ['numPMIDsupportingAssociation', 'pmid', 'numPublications'],
      ['pmid', 'publication'],
    );
    const chem = getRowValueByPriority(
      row,
      ['numberChemsIncludedInEvidence', 'chemPrefName', 'chemID', 'chemIDs', 'chemsInSentence'],
      ['chem'],
    );
    const variant = getRowValueByPriority(
      row,
      ['variantStrID', 'dbSNP', 'hgvs'],
      ['variant', 'dbsnp', 'hgv'],
    );

    return {
      leftEntity: normalizeValue(leftEntity),
      rightEntity: rightEntity == null ? '' : normalizeValue(rightEntity),
      showRelationArrow,
      diseaseType: normalizeValue(diseaseType),
      score: normalizeValue(score),
      pmids: normalizeValue(pmids),
      chem: String(countItems(chem)),
      variant: normalizeValue(variant),
    };
  }, [bottomDetailCellValues, bottomDetailGtpOnly, bottomDetailRow, tab]);

  const columnOrder = useMappingSidebarState
    ? mappingSidebar.state!.columnOrder
    : activeColumnPanel === 'summary'
      ? summaryColumnOrder
      : evidenceColumnOrder;

  // ─── Visibility logic ─────────────────────────────────────────────────────

  const entityListTabs = ['DISEASES', 'GENES', 'VARIANTS', 'CHEMICALS'];
  const graphVisibilityOptions = { searchItemCount: searchItems?.length ?? 0 };
  const showManageGraph = searchTabHasGraphs(view as string, tab as string, graphVisibilityOptions);

  const urlParams = new URLSearchParams(window.location.search);
  const isEvidenceParam = urlParams.get('isEvidence');

  let showSummary = true;
  let showEvidence = true;

  if (isEvidenceParam === 'false') {
    showEvidence = false;
  } else if (isEvidenceParam === 'true') {
    showSummary = false;
  } else {
    if (tab === 'DDA') showEvidence = false;
    if (entityListTabs.includes(tab as string)) showEvidence = false;
    if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) showEvidence = false;
  }

  if (entityListTabs.includes(tab as string)) {
    showEvidence = false;
    showSummary = true;
  }
  if (view === 'ALL' && !['GDA', 'VDA'].includes(tab as string)) {
    showEvidence = false;
    showSummary = true;
  }

  const searchTableSummaryEvidenceToggle =
    showSummary && showEvidence
      ? {
          showSummary: true as const,
          showEvidence: true as const,
          active: activeColumnPanel,
          onChange: setActiveColumnPanel,
        }
      : showSummary && !showEvidence
        ? {
            showSummary: true as const,
            showEvidence: false as const,
            active: 'summary' as const,
            onChange: () => {},
          }
        : !showSummary && showEvidence
          ? {
              showSummary: false as const,
              showEvidence: true as const,
              active: 'evidence' as const,
              onChange: () => {},
            }
          : undefined;

  const visibleTabs = getVisibleSearchTabs(tabList, selectedView, view as string, graphVisibilityOptions);

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (savedColumsApplied) {
      showSuccessAlert(`Column configuration "${savedColumsApplied.name}" applied successfully`);
      setSavedColumsApplied(null);
    }
  }, [savedColumsApplied]);

  const displayParam = searchParams.get('display');
  useEffect(() => {
    setSelectedView(displayParam === 'graphs' ? 'graphs' : 'tables');
  }, [displayParam]);

  useEffect(() => {
    if (!showEvidence) setActiveColumnPanel('summary');
  }, [showEvidence]);

  useEffect(() => {
    if (!showSummary || !showEvidence) return;
    const params = new URLSearchParams(window.location.search);
    const activePanel = params.get('activePanel');
    if (activePanel === 'evidence') {
      setActiveColumnPanel('evidence');
      return;
    }
    if (activePanel === 'summary') {
      setActiveColumnPanel('summary');
      return;
    }
    const v = params.get('isEvidence');
    if (v === 'true') setActiveColumnPanel('evidence');
    else if (v === 'false') setActiveColumnPanel('summary');
  }, [showSummary, showEvidence, tab, view, searchParams]);

  useEffect(() => {
    if (!isFullRecordMode || bottomDetailRow) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('fullRecord');
    setSearchParams(nextParams);
  }, [isFullRecordMode, bottomDetailRow, searchParams, setSearchParams]);

  useEffect(() => {
    return () => {
      setScoreBreakdownRow(null);
      setPrevalenceBreakdownRow(null);
      setEvidenceTimelineRow(null);
    };
  }, []);

  useEffect(() => {
    if (scoreBreakdownRow) {
      setLeftInfoPanelItem(null);
      setLeftSentencePanel(null);
      setPrevalenceBreakdownRow(null);
    }
  }, [scoreBreakdownRow]);

  useEffect(() => {
    if (prevalenceBreakdownRow) {
      setLeftInfoPanelItem(null);
      setLeftSentencePanel(null);
      setScoreBreakdownRow(null);
    }
  }, [prevalenceBreakdownRow]);

  useEffect(() => {
    const newIndex = tabList.findIndex(t => t.key === tab);
    setTabIndex(newIndex);
    const stored = sessionStorage.getItem(`sourceWeights_${tab}`);
    if (stored) {
      try {
        setSourceWeights(JSON.parse(stored));
      } catch {
        setSourceWeights([]);
      }
    } else {
      setSourceWeights([]);
    }
  }, [tab, tabList]);

  useEffect(() => {
    if (prevTabForResetRef.current === null || prevTabForResetRef.current === tab) {
      prevTabForResetRef.current = tab as string;
      return;
    }
    prevTabForResetRef.current = tab as string;
    setSummaryColumnModel(undefined);
    setEvidenceColumnModel(undefined);
    setSummaryColumnOrder(undefined);
    setEvidenceColumnOrder(undefined);
    if (!shouldPreserveBottomDetail()) {
      setBottomDetailRow(null);
      setBottomDetailCellValues(null);
      setBottomDetailColumnLabels(null);
      setBottomDetailColumnCategories(null);
    }
  }, [tab]);

  useEffect(() => {
    const current = tab as string;
    if (prevTabForMappingRef.current !== null && prevTabForMappingRef.current !== current) {
      collapseMappingToRoot();
    }
    prevTabForMappingRef.current = current;
  }, [tab, collapseMappingToRoot]);

  useLayoutEffect(() => {
    const prevIndex = prevMappingIndexRef.current;
    prevMappingIndexRef.current = mappingStackCurrentIndex;
    if (!(prevIndex > 0 && mappingStackCurrentIndex === 0)) return;

    const urlIdents = searchParams.get('idents') || '';
    const urlView = searchParams.get('view') || (view as string);

    const restorePanel = (
      isEvidence: boolean,
      setModel: typeof setSummaryColumnModel,
      setOrder: typeof setSummaryColumnOrder,
    ) => {
      const defaults = ColumnVisibilityModels.getByViewTab(
        view as string,
        tab as string,
        isEvidence,
        searchItems,
        urlIdents,
        urlView,
      ) as Record<string, boolean> | undefined;
      if (!defaults) return;
      setModel(prev => applyEntityColumnDefaults(prev, defaults));
      setOrder(prev => mergeEntityColumnsIntoOrder(prev, defaults));
    };

    restorePanel(false, setSummaryColumnModel, setSummaryColumnOrder);
    restorePanel(true, setEvidenceColumnModel, setEvidenceColumnOrder);
  }, [mappingStackCurrentIndex, view, tab, searchItems, searchParams]);

  useEffect(() => {
    if (mappingUrlRestoreDoneRef.current) return;
    mappingUrlRestoreDoneRef.current = true;
    applyMappingStateFromSearch(window.location.search);
    setTimeout(() => setIsMappingUrlHydrated(true), 0);
  }, []);

  useEffect(() => {
    if (!isMappingUrlHydrated) return;
    if (isProgrammaticSearchNavigationPending()) return;
    const params = new URLSearchParams(window.location.search);
    const current = params.get(MAPPING_URL_PARAM);
    const hasDrillDown = mappingStack.length > 1 && mappingStackCurrentIndex > 0;
    if (!hasDrillDown) {
      if (skipMappingUrlParamStripRef.current) {
        queueMicrotask(() => {
          skipMappingUrlParamStripRef.current = false;
        });
        return;
      }
      if (current != null) {
        params.delete(MAPPING_URL_PARAM);
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}?${params.toString()}${window.location.hash}`,
        );
      }
      return;
    }
    const encoded = encodeMappingStateForUrl({
      stack: mappingStack,
      currentIndex: mappingStackCurrentIndex,
      tableStateByLevelId: mappingTableStateByLevelId,
    });
    if (current === encoded) return;
    params.set(MAPPING_URL_PARAM, encoded);
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}?${params.toString()}${window.location.hash}`,
    );
  }, [mappingStack, mappingStackCurrentIndex, mappingTableStateByLevelId, isMappingUrlHydrated]);

  useEffect(() => {
    const onOpenBottomDetail = (event: Event) => {
      const customEvent = event as CustomEvent<{
        original?: any;
        values?: Record<string, any>;
        labels?: Record<string, string>;
        categories?: Record<string, string>;
        initialTab?: BottomDetailTab;
        gtpOnly?: boolean;
      } | null>;
      const detail = customEvent.detail ?? null;
      const initialTab = detail && typeof detail === 'object' ? detail.initialTab : undefined;
      const gtpOnly = Boolean(detail && typeof detail === 'object' && detail.gtpOnly);
      const resolvedInitialTab: BottomDetailTab =
        initialTab === 'summary' || initialTab === 'historical' || initialTab === 'gtp'
          ? initialTab
          : 'summary';
      preferredBottomDetailTabRef.current = resolvedInitialTab;
      setBottomDetailGtpOnly(gtpOnly);
      setBottomDetailTab(resolvedInitialTab);
      setIsHistoricalEvidencePeek(false);
      if (detail && typeof detail === 'object' && ('original' in detail || 'values' in detail)) {
        setBottomDetailRow(detail.original ?? null);
        setBottomDetailCellValues(detail.values ?? null);
        setBottomDetailColumnLabels(detail.labels ?? null);
        setBottomDetailColumnCategories(detail.categories ?? null);
        const hasGtp =
          isRelease263Enabled &&
          (getGtpItemsFromRow(detail.original).length > 0 || getGtpItemsFromRow(detail.values).length > 0);
        setBottomDetailHeight(hasGtp || gtpOnly || resolvedInitialTab === 'gtp' ? 480 : 300);
      } else {
        setBottomDetailRow(detail);
        setBottomDetailCellValues(null);
        setBottomDetailColumnLabels(null);
        setBottomDetailColumnCategories(null);
        setBottomDetailHeight(300);
      }
    };
    window.addEventListener('disgenet:open-bottom-row-detail', onOpenBottomDetail);
    return () => window.removeEventListener('disgenet:open-bottom-row-detail', onOpenBottomDetail);
  }, []);

  useEffect(() => {
    if (shouldPreserveBottomDetail()) return;
    const preferred = preferredBottomDetailTabRef.current ?? 'summary';
    preferredBottomDetailTabRef.current = null;
    setBottomDetailTab(preferred);
    setIsHistoricalEvidencePeek(false);
  }, [bottomDetailRowTabKey]);

  useEffect(() => {
    if (!bottomDetailRow) setBottomDetailGtpOnly(false);
  }, [bottomDetailRow]);

  useEffect(() => {
    const handleOpenInfoPanel = (event: Event) => {
      const customEvent = event as CustomEvent<{ item?: any }>;
      const item = customEvent.detail?.item;
      if (!item) return;
      handleOpenEntityInfo(item);
    };
    window.addEventListener('disgenet:open-left-info-panel', handleOpenInfoPanel);
    return () => window.removeEventListener('disgenet:open-left-info-panel', handleOpenInfoPanel);
  }, []);

  useEffect(() => {
    const handleOpenSentencePanel = (event: Event) => {
      const customEvent = event as CustomEvent<{ content?: string; allowCopy?: boolean }>;
      const content = customEvent.detail?.content;
      if (!content) return;
      setScoreBreakdownRow(null);
      setPrevalenceBreakdownRow(null);
      setEvidenceTimelineRow(null);
      setLeftInfoPanelItem(null);
      setLeftSentencePanel({ content, allowCopy: Boolean(customEvent.detail?.allowCopy) });
    };
    window.addEventListener('disgenet:open-left-sentence-panel', handleOpenSentencePanel);
    return () => window.removeEventListener('disgenet:open-left-sentence-panel', handleOpenSentencePanel);
  }, []);

  useEffect(() => {
    if (!SHOW_HISTORIAL_SCORE) return;
    const onFocusHistorical = () => {
      if (bottomDetailRowRef.current && !isAcademic) setBottomDetailTab('historical');
    };
    window.addEventListener('disgenet:focus-bottom-detail-historical-tab', onFocusHistorical);
    return () => window.removeEventListener('disgenet:focus-bottom-detail-historical-tab', onFocusHistorical);
  }, [isAcademic]);

  useEffect(() => {
    if (geneContent?.isActive && geneContent.geneInfo?.id) {
      handleOpenEntityInfo({
        id: geneContent.geneInfo.id,
        symbol: geneContent.geneInfo.symbol,
        entityView: 'GENES',
      });
      setGeneContent({ isActive: false, geneInfo: null });
    }
  }, [geneContent?.isActive]);

  useEffect(() => {
    if (mappingContent!.isActive) scrollToRef('Mapping');
  }, [mappingContent!.isActive]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const activePanel = params.get('activePanel');
    if (activePanel === 'summary' || activePanel === 'evidence') return;
    if (params.get('loadComplete')) return;

    let hasEvidenceFilters = false;
    let hasSummaryFilters = false;
    for (const [key] of params.entries()) {
      if (key.startsWith('filters-evidence-') || key.startsWith('filters[evidence]['))
        hasEvidenceFilters = true;
      if (key.startsWith('filters-summary-') || key.startsWith('filters[summary][')) hasSummaryFilters = true;
    }
    if (hasEvidenceFilters && !hasSummaryFilters && showEvidence) {
      if (showSummary) setActiveColumnPanel('evidence');
      setTimeout(() => scrollToRef('SearchTable'), 300);
    } else if (hasSummaryFilters && !hasEvidenceFilters && showSummary) {
      setActiveColumnPanel('summary');
    }
  }, [showEvidence, showSummary, searchParams]);

  useEffect(() => {
    if (
      prevSearchItemsLengthRef.current !== null &&
      prevSearchItemsLengthRef.current > 0 &&
      searchItems?.length === 0
    ) {
      setSelectedView('tables');
      setSearchParams(
        prev => {
          const u = new URLSearchParams(prev);
          u.delete('display');
          return u;
        },
        { replace: true },
      );
    }
    prevSearchItemsLengthRef.current = searchItems?.length ?? 0;
  }, [searchItems]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const scrollToRef = (ref: string) => {
    scroller.scrollTo(ref, { duration: 750, smooth: true });
  };

  const handleOpenEntityInfo = (item: any) => {
    setScoreBreakdownRow(null);
    setPrevalenceBreakdownRow(null);
    setLeftSentencePanel(null);
    setLeftInfoPanelItem(item);
  };

  const handleCopySentenceFromPanel = async () => {
    if (!sentenceToCopyRef.current) return;
    try {
      await navigator.clipboard.writeText(sentenceToCopyRef.current.textContent || '');
      setSentenceCopied(true);
      setTimeout(() => setSentenceCopied(false), 3000);
    } catch {
      /* silent */
    }
  };

  const handleApplyWeights = (weights: Array<{ name: string; score: number }>) => {
    setSourceWeights(weights);
    if (weights.length > 0) {
      sessionStorage.setItem(`sourceWeights_${tab}`, JSON.stringify(weights));
    } else {
      sessionStorage.removeItem(`sourceWeights_${tab}`);
    }
  };

  const handleApplySavedScore = (savedScore: any) => {
    if (savedScore.sourceWeights && savedScore.sourceWeights.length > 0) {
      handleApplyWeights(savedScore.sourceWeights);
      const urlParams = new URLSearchParams(window.location.search);
      if (savedScore.sourceWeights.length > 1) urlParams.set('source', 'ALL');
      else if (savedScore.sourceWeights.length === 1)
        urlParams.set('source', savedScore.sourceWeights[0].name);
      window.history.pushState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
      window.location.reload();
    }
  };

  const handleDeleteSavedScore = async (scoreId: string) => {
    try {
      if (!scoreId.startsWith('savedScore_')) await deleteFilter(scoreId);
    } catch (error) {
      console.error('Error deleting saved score:', error);
    }
  };

  const handleClearWeights = () => {
    setSourceWeights([]);
    sessionStorage.removeItem(`sourceWeights_${tab}`);
    window.location.reload();
  };

  const handleApplySavedColumns = (savedColumn: any) => {
    if (savedColumn.columnVisibilityModel) {
      if (activeColumnPanel === 'summary') setSummaryColumnModel(savedColumn.columnVisibilityModel);
      else setEvidenceColumnModel(savedColumn.columnVisibilityModel);
    }
    if (savedColumn.columnOrder) {
      if (activeColumnPanel === 'summary') setSummaryColumnOrder(savedColumn.columnOrder);
      else setEvidenceColumnOrder(savedColumn.columnOrder);
    }
    setSavedColumsApplied({
      name: savedColumn.name,
      columnVisibilityModel: savedColumn.columnVisibilityModel,
      columnOrder: savedColumn.columnOrder,
    });
  };

  const handleDeleteSavedColumn = async (columnId: string) => {
    try {
      if (!columnId.startsWith('columnSelection_')) await deleteFilter(columnId);
    } catch (error) {
      console.error('Error deleting column configuration:', error);
    }
  };

  const handleResetToDefault = () => {
    if (useMappingSidebarState && mappingSidebar.state) {
      mappingSidebar.state.setColumnVisibilityModel(() => ({}));
      mappingSidebar.state.setColumnOrder([]);
    } else if (activeColumnPanel === 'summary') {
      summaryResetRef.current?.();
    } else {
      evidenceResetRef.current?.();
    }
  };

  const handleTabSelect = (index: number) => {
    setTabIndex(index);
    const newTab = tabList[index].key;
    const tabLabel = tabList[index].label;
    trackBrowseTabClick(tabLabel);
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('tab', newTab);
    navigate({ pathname: window.location.pathname, search: `?${currentParams.toString()}` });
  };

  const handleRemoveSearchItem = (index: number) => {
    const remaining = (searchItems ?? []).filter((_: any, i: number) => i !== index);
    if (remaining.length === 0) {
      skipMappingUrlParamStripRef.current = true;
      clearPendingSearchRedirect();
      navigate('/', { replace: true });
      clearMappingStack(0);
    } else {
      const params = new URLSearchParams(window.location.search);
      const newParams = updateParams({
        searchItems: remaining as any,
        view: params.get('view') ?? undefined,
        source: params.get('source') ?? undefined,
        tab: params.get('tab') ?? undefined,
      });
      navigate({ pathname: window.location.pathname, search: newParams });
    }
  };

  const handleClearAllSearchItems = () => {
    skipMappingUrlParamStripRef.current = true;
    clearPendingSearchRedirect();
    navigate('/', { replace: true });
    clearMappingStack(0);
  };

  const handleDownloadRowDetail = useCallback(() => {
    if (!bottomDetailRow && !bottomDetailCellValues) return;
    const now = new Date();
    const stamp = now.toISOString().replace(/[:.]/g, '-');
    const fileBase = `${String(tab || 'row').toLowerCase()}-row-detail-${stamp}`;
    const escapeCsvCell = (value: unknown): string => {
      const raw = value == null ? '' : String(value);
      return `"${raw.replace(/"/g, '""')}"`;
    };
    const rows: string[] = [];
    rows.push(['Section', 'Field', 'Value'].map(escapeCsvCell).join(','));
    rows.push(['Context', 'Exported at', now.toISOString()].map(escapeCsvCell).join(','));
    rows.push(['Context', 'Tab', String(tab ?? '')].map(escapeCsvCell).join(','));
    rows.push(['Context', 'View', String(view ?? '')].map(escapeCsvCell).join(','));
    rows.push(['Context', 'Source', String(source ?? '')].map(escapeCsvCell).join(','));
    rows.push(['Context', 'Label', getRowDetailContextLabel(tab as string)].map(escapeCsvCell).join(','));
    rows.push(['Summary', 'Primary entity', relationSummary.leftEntity].map(escapeCsvCell).join(','));
    if (relationSummary.showRelationArrow && relationSummary.rightEntity) {
      rows.push(['Summary', 'Secondary entity', relationSummary.rightEntity].map(escapeCsvCell).join(','));
    }
    rows.push(['Summary', 'Disease type', relationSummary.diseaseType].map(escapeCsvCell).join(','));
    rows.push(['Summary', 'Score', relationSummary.score].map(escapeCsvCell).join(','));
    rows.push(['Summary', 'PMIDs', relationSummary.pmids].map(escapeCsvCell).join(','));
    rows.push(['Summary', 'Chemicals', relationSummary.chem].map(escapeCsvCell).join(','));
    rows.push(['Summary', 'Variant', relationSummary.variant].map(escapeCsvCell).join(','));
    groupedBottomDetails.forEach(group => {
      group.entries.forEach(entry => {
        rows.push(
          [group.title, entry.displayLabel, normalizeValue(entry.value, entry.key)]
            .map(escapeCsvCell)
            .join(','),
        );
      });
    });
    const gtpItems = isRelease263Enabled ? getGtpItemsFromRow(bottomDetailRow) : [];
    const gtpFromCells = isRelease263Enabled ? getGtpItemsFromRow(bottomDetailCellValues) : [];
    const gtpForCsv = gtpItems.length > 0 ? gtpItems : gtpFromCells;
    if (gtpForCsv.length > 0) {
      rows.push(['GTAP', 'Profile', formatGtpList(gtpForCsv)].map(escapeCsvCell).join(','));
    }
    const csvContent = `\uFEFF${rows.join('\n')}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${fileBase}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }, [bottomDetailRow, bottomDetailCellValues, groupedBottomDetails, relationSummary, tab, view, source]);

  const handleSetBottomDetailTab = useCallback((next: BottomDetailTab) => {
    setBottomDetailTab(next);
    if (next === 'summary' || next === 'gtp') {
      setIsHistoricalEvidencePeek(false);
      setBottomDetailHeight(prev => (prev <= HISTORICAL_SCORE_PEEK_HEIGHT ? 300 : prev));
    }
  }, []);

  const startBottomResize = (startY: number) => {
    const startHeight = bottomDetailHeight;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
    const onMove = (clientY: number) =>
      setBottomDetailHeight(Math.min(700, Math.max(180, startHeight + (startY - clientY))));
    const onMouseMove = (event: MouseEvent) => onMove(event.clientY);
    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length) onMove(event.touches[0].clientY);
    };
    const onEnd = () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
  };

  const ensureTablesViewForDrilldown = useCallback(() => {
    if (selectedView === 'tables') return;
    setSelectedView('tables');
    setSearchParams(
      prev => {
        const u = new URLSearchParams(prev);
        u.delete('display');
        return u;
      },
      { replace: true },
    );
  }, [selectedView, setSearchParams]);

  useEffect(() => {
    if (selectedView === 'graphs' && !showManageGraph) {
      ensureTablesViewForDrilldown();
    }
  }, [ensureTablesViewForDrilldown, selectedView, showManageGraph]);

  const closeBottomDetail = useCallback(() => {
    setBottomDetailRow(null);
    setBottomDetailCellValues(null);
    setBottomDetailColumnLabels(null);
    setBottomDetailColumnCategories(null);
    setBottomDetailTab('summary');
    setBottomDetailGtpOnly(false);
    setIsHistoricalEvidencePeek(false);
    clearHistoricalScoreSession();
  }, []);

  const revealEvidenceTableFromHistoricalScore = useCallback(() => {
    ensureTablesViewForDrilldown();
    setBottomDetailTab('historical');
    setIsHistoricalEvidencePeek(true);
    setBottomDetailHeight(HISTORICAL_SCORE_PEEK_HEIGHT);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        if (next.get('fullRecord') !== '1') return prev;
        next.delete('fullRecord');
        return next;
      },
      { replace: true },
    );
  }, [ensureTablesViewForDrilldown, setSearchParams]);

  useEffect(() => {
    const paramsWithoutFullRecord = new URLSearchParams(searchParams);
    paramsWithoutFullRecord.delete('fullRecord');
    const detailContextSignature = paramsWithoutFullRecord.toString();
    if (previousDetailContextSignatureRef.current === null) {
      previousDetailContextSignatureRef.current = detailContextSignature;
      return;
    }
    const didContextChange = previousDetailContextSignatureRef.current !== detailContextSignature;
    if (didContextChange && (bottomDetailRow || bottomDetailCellValues)) {
      if (shouldPreserveBottomDetail()) {
        clearBottomDetailPreservation();
      } else {
        closeBottomDetail();
      }
    }
    previousDetailContextSignatureRef.current = detailContextSignature;
  }, [searchParams, bottomDetailRow, bottomDetailCellValues, closeBottomDetail]);

  const closeSentencePanel = () => setLeftSentencePanel(null);

  return {
    // Context
    view,
    source,
    tab,
    searchItems,
    setSearchItems,
    mappingContent,
    restrictChemical,
    isAuthenticated,
    // Auth
    isTrial,
    isAcademic,
    // Sidebar layout
    isSidebarOpen,
    sidebarWidth,
    setSidebarOpen,
    isDesktop,
    // State
    tabIndex,
    selectedView,
    isInfoSidebarOpen,
    setIsInfoSidebarOpen,
    scoreModalOpen,
    setScoreModalOpen,
    refreshScoresDropdown,
    setRefreshScoresDropdown,
    leftInfoPanelItem,
    leftSentencePanel,
    bottomDetailTab,
    setBottomDetailTab: handleSetBottomDetailTab,
    bottomDetailGtpOnly,
    bottomDetailRow,
    bottomDetailCellValues,
    bottomDetailHeight,
    setBottomDetailHeight,
    isHistoricalEvidencePeek,
    sentenceCopied,
    sourceWeights,
    graphsColumnModel,
    setGraphsColumnModel,
    summaryColumnModel,
    setSummaryColumnModel,
    summaryColumnOrder,
    setSummaryColumnOrder,
    evidenceColumnModel,
    setEvidenceColumnModel,
    evidenceColumnOrder,
    setEvidenceColumnOrder,
    activeColumnPanel,
    setActiveColumnPanel,
    isMappingUrlHydrated,
    // Refs
    sentenceToCopyRef,
    summaryResetRef,
    evidenceResetRef,
    // Computed
    tabList,
    visibleTabs,
    searchItemsEntityPlural,
    groupedBottomDetails,
    relationSummary,
    columnOrder,
    searchTableSummaryEvidenceToggle,
    isFullRecordMode,
    showSummary,
    showEvidence,
    showManageGraph,
    isInMappingView,
    useMappingSidebarState,
    useDrillDownMapping,
    currentMappingLevel,
    mappingSidebar,
    // Breakdown rows
    scoreBreakdownRow,
    setScoreBreakdownRow,
    prevalenceBreakdownRow,
    setPrevalenceBreakdownRow,
    evidenceTimelineRow,
    setEvidenceTimelineRow,
    // Handlers
    handleTabSelect,
    handleRemoveSearchItem,
    handleClearAllSearchItems,
    handleOpenEntityInfo,
    handleCopySentenceFromPanel,
    handleApplyWeights,
    handleApplySavedScore,
    handleDeleteSavedScore,
    handleClearWeights,
    handleApplySavedColumns,
    handleDeleteSavedColumn,
    handleResetToDefault,
    handleDownloadRowDetail,
    startBottomResize,
    closeBottomDetail,
    revealEvidenceTableFromHistoricalScore,
    ensureTablesViewForDrilldown,
    closeSentencePanel,
    searchParams,
    setSearchParams,
    skipMappingUrlParamStripRef,
    defaultFilterValue,
  };
}
